import ko from "knockout";
import _ from "lodash";
import { Models } from "purecloud-platform-client-v2";
import ViewModel from "viewModels/ViewModelTyped";
import localeService from "services/localeService";
import loggingService from "services/loggingService";
import qualityBroadcast from "services/qualityBroadcast";
import config from "config";
import { getDecodedQueryParamsFromWindow } from "util/uriUtil";
import speechAndTextAnalyticsService from "services/speechAndTextAnalyticsService";
import toastService from "services/toastService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
const recordingFileRoleCustomerExperience = "CUSTOMER_EXPERIENCE";

type HostToChildTopic =
    | "environmentalData"
    | "conversationDetails"
    | "activeCommunication"
    | "jumpToPhrase"
    | "playerSynchronization"
    | "fetchRecordingsError"
    | "recordingDetails"
    | "conversationRecordings"
    | "jumpToEmailTopicPhrase";

interface HostToChildPublication {
    topic: HostToChildTopic;
    data: any;
}

interface ActiveCommunication {
    activeCommunicationId: string | undefined;
    mediaType: string | undefined;
    recordingId: string | undefined;
    recordingStartTime: number | undefined;
    recordingEndTime: number | undefined;
    pauses: Pause[];
}

interface AnnotationClickEvent {
    phraseId: number;
    topicNumber: number | undefined;
}

interface PlayerSynchEvent {
    currentPositionMs: number;
    isPlaying: boolean;
    hasPriority: boolean;
}

interface Pause {
    location: number | undefined;
    absoluteDurationMs: number | undefined;
    pauseReason: string | undefined;
}

interface PhraseClickMessage {
    offsetWords: number;
    topicPhrase: string;
    startTimeMs: number;
}

interface PauseAnnotation {
    id?: string;
    name?: string;
    type?: string;
    durationMs?: number;
    absoluteLocation?: number;
    location?: number;
    absoluteDurationMs?: number;
    recordingLocation?: number;
    recordingDurationMs?: number;
    user?: Models.User;
    description?: string;
    selfUri?: string;
    reason?: string;
}

export enum ToastNotificationTypeEnum {
    MESSAGE = "MESSAGE",
    MINOR_ALERT = "MINOR_ALERT",
    MAJOR_ALERT = "MAJOR_ALERT",
    CRITICAL_ALERT = "CRITICAL_ALERT",
}

export default class GIATranscriptView extends ViewModel {
    private queryParams = getDecodedQueryParamsFromWindow();
    public transcriptUiURL: ko.Computed<string>;
    public isLoading: ko.Observable<boolean>;
    public fetchRecordingsErrorStatus: ko.Observable<number>;
    public conversationRecordings: ko.ObservableArray<Models.Recording>;
    public transcriptAriaLabel = ko.i18n.renderString(
        "playback.nav.transcript"
    );

    private childIFrameElement: HTMLIFrameElement | undefined;
    private conversationDetailsV6: ko.Computed<Models.AnalyticsConversation>;
    private mediaType: ko.Computed<string | undefined>;
    private recording: ko.Computed<Models.Recording>;
    private isActiveInteraction: ko.Computed<boolean>;
    private hasRecordingViewContent: ko.Computed<boolean | undefined>;
    private transcriptUIReady = ko.observable(false);

    constructor(
        conversationDetailsV6: ko.Computed<Models.AnalyticsConversation>,
        isTranscriptLoading: ko.Observable<boolean>,
        fetchRecordingsErrorStatus: ko.Observable<number>,
        recording: ko.Computed<Models.Recording>,
        conversationRecordings: ko.ObservableArray<Models.Recording>,
        isActiveInteraction?: ko.Computed<boolean>,
        hasRecordingViewContent?: ko.Computed<boolean | undefined>
    ) {
        super();
        this.conversationDetailsV6 = conversationDetailsV6;
        this.isLoading = isTranscriptLoading;
        this.fetchRecordingsErrorStatus = fetchRecordingsErrorStatus;
        this.recording = recording;
        this.conversationRecordings = conversationRecordings;
        this.isActiveInteraction =
            isActiveInteraction ?? ko.pureComputed(() => false);
        this.hasRecordingViewContent =
            hasRecordingViewContent ??
            ko.pureComputed((): boolean | undefined => undefined);

        // Compute mediaType from recording.
        // Returns "unavailable" when:
        //   - isActiveInteraction is true (live interaction), OR
        //   - hasRecordingViewContent is defined and false (activeRecordingView exists but has no playable content) e.g. 404 case.
        // Returns undefined when hasRecordingViewContent is undefined (no activeRecordingView yet)
        // Returns the actual media type when recording is available.
        this.mediaType = ko.pureComputed(() => {
            const recording = this.recording();
            if (recording?.media) {
                return ko.unwrap(recording.media);
            }
            const hasContent = this.hasRecordingViewContent();
            if (
                this.isActiveInteraction() ||
                (hasContent !== undefined && !hasContent)
            ) {
                return "unavailable";
            }
            return undefined;
        });

        // Computed URL that switches between transcript-ui and transcript-ui-v2
        // based on feature toggle and media type
        this.transcriptUiURL = this.computed(() => {
            const isEmailTranscriptV2Enabled = featureToggleService.isEnabled(
                FeatureToggle.EMAIL_TRANSCRIPT_UI_V2
            );
            const currentMediaType = this.mediaType();
            const isEmailInteraction = currentMediaType === "email";

            const baseConfig =
                isEmailTranscriptV2Enabled && isEmailInteraction
                    ? config.giaTranscriptV2
                    : config.giaTranscript;

            // Apply version override from query params if provided
            return this.queryParams.giaTranscriptVersion
                ? baseConfig.uri + "/" + this.queryParams.giaTranscriptVersion
                : baseConfig.uri;
        });

        window.addEventListener("message", (event) => {
            this.handleTranscriptUIReady(this, event);
        });
        this.transcriptUIReady.subscribe(
            this.handleTranscriptUIReadyChanged.bind(this)
        );
    }

    private handleTranscriptUIReadyChanged() {
        if (this.transcriptUIReady()) {
            this.publishTranscriptUIData();
            this.transcriptUIReady(false);
        }
    }

    private publishActiveCommunication(
        mediaType: string | undefined,
        recording: Models.Recording | undefined
    ) {
        if (mediaType !== "email") {
            if (!recording) {
                // live recording and message will not have recording
                if (mediaType) {
                    this.publish({
                        topic: "activeCommunication",
                        data: {
                            mediaType: mediaType,
                            activeCommunicationId: undefined,
                            pauses: [],
                            recordingId: undefined,
                            recordingStartTime: undefined,
                            recordingEndTime: undefined,
                        },
                    });
                }
                // wait until media type and recording are available before publishing
                return;
            }
            const startTimeIso = recording.startTime
                ? new Date(recording.startTime)
                : undefined;
            const endTimeIso = recording.endTime
                ? new Date(recording.endTime)
                : undefined;
            const activeCommunicationId =
                this.getActiveCommunicationId(recording);

            const pauseData: Pause[] = [];
            _.each(
                recording.annotations,
                function (annotation: PauseAnnotation) {
                    if (annotation.type === "Pause") {
                        pauseData.push({
                            location: annotation.recordingLocation,
                            absoluteDurationMs: annotation.absoluteDurationMs,
                            pauseReason: annotation.reason,
                        });
                    }
                }
            );

            const data: ActiveCommunication = {
                mediaType: mediaType,
                activeCommunicationId: activeCommunicationId,
                pauses: pauseData,
                recordingId: recording.id,
                recordingStartTime: startTimeIso
                    ? startTimeIso.getTime()
                    : undefined,
                recordingEndTime: endTimeIso ? endTimeIso.getTime() : undefined,
            };

            this.publish({
                topic: "activeCommunication",
                data: data,
            });

            if (mediaType === "message" || mediaType === "chat") {
                this.publish({
                    topic: "recordingDetails",
                    data: JSON.parse(JSON.stringify(recording)),
                });
            }
        }

        if (mediaType === "email") {
            /*
             * Cloning the object before publishing it as recording object contains functions
             * and objects with functions cannot be cloned using the structured clone algorithm
             * which is used by window.postMessage()
             */
            const conversationRecordingsForEmail =
                this.conversationRecordings().filter(
                    (conversation) =>
                        conversation.media &&
                        conversation.media.toLocaleLowerCase() === "email"
                );
            this.publish({
                topic: "conversationRecordings",
                data: JSON.parse(
                    JSON.stringify(conversationRecordingsForEmail)
                ),
            });
        }
    }

    private getActiveCommunicationId(recording: Models.Recording) {
        if (
            recording.recordingFileRole ===
                recordingFileRoleCustomerExperience &&
            this.checkParticipantPurposeAgent(recording)
        ) {
            return undefined;
        } else {
            return ko.unwrap(recording.sessionId);
        }
    }

    private checkParticipantPurposeAgent(recording: Models.Recording) {
        let isAgent = false;
        if (
            this.conversationDetailsV6 &&
            this.conversationDetailsV6().participants
        ) {
            _.each(
                this.conversationDetailsV6().participants,
                function (participant) {
                    if (
                        participant.purpose &&
                        participant.purpose.toLowerCase() === "agent"
                    ) {
                        _.each(participant.sessions, function (session) {
                            if (
                                !isAgent &&
                                session.sessionId === recording.sessionId
                            ) {
                                isAgent = true;
                            }
                        });
                    }
                }
            );
        }
        return isAgent;
    }

    private publishJumpToPhrase(phraseId: number | AnnotationClickEvent) {
        this.publish({
            topic: "jumpToPhrase",
            data: phraseId,
        });
    }

    private publishUpdatePosition(annotationClickEvent: PlayerSynchEvent) {
        this.publish({
            topic: "playerSynchronization",
            data: annotationClickEvent,
        });
    }

    private publishJumpToEmailPhrase(message: PhraseClickMessage) {
        this.publish({
            topic: "jumpToEmailTopicPhrase",
            data: message,
        });
    }

    private publish(data: HostToChildPublication) {
        if (!this.childIFrameElement) {
            loggingService.error("Could not find iframe element to publish to");
            return;
        }
        const { contentWindow } = this.childIFrameElement;
        if (!contentWindow) {
            loggingService.error("Failed to get child frame contentWindow");
            return;
        }
        contentWindow.postMessage(data, this.transcriptUiURL());
    }

    private publishTranscriptUIData() {
        this.publish({
            topic: "fetchRecordingsError",
            data: this.fetchRecordingsErrorStatus(),
        });
        const environmentalData: {
            [key: string]: string | undefined;
        } = {};

        environmentalData.locale = localeService.getAppLocale();

        if (ko.i18n.getTimezone()) {
            environmentalData.timezone = ko.i18n.getTimezone();
        }

        this.publish({
            topic: "environmentalData",
            data: {
                ...environmentalData,
            },
        });

        this.publish({
            topic: "conversationDetails",
            data: this.conversationDetailsV6(),
        });

        // Used for user switch back to transcript tab, after switching to a different tab.
        if (this.mediaType()) {
            this.publishActiveCommunication(this.mediaType(), this.recording());
        }
    }

    private handleTranscriptUIReady(
        self: GIATranscriptView,
        event: MessageEvent
    ) {
        if (!event) {
            return;
        }
        const payload = event.data;
        if (payload.key == "transcriptUIReady") {
            self.transcriptUIReady(true);
        }
    }

    private handleMessageEvent(event: any) {
        if (!event) {
            return;
        }
        const payload = event.data;
        if (payload.key && payload.key === "jumpToLocation") {
            event.preventDefault();
            qualityBroadcast.publish(
                this.recording().id,
                "transcriptUIJumpToLocation",
                payload
            );
        }
        if (payload.key && payload.key === "transcriptUIPlayControlRequest") {
            qualityBroadcast.publish(
                this.recording().id,
                "transcriptUIPlayControlRequest",
                payload.transfer
            );
        }
        if (payload.key && payload.key === "evaluationViewPhraseSelect") {
            if (payload.emailPhrase) {
                this.publishJumpToEmailPhrase(payload.emailPhrase);
            } else {
                const { topicNumber, phraseId, location } =
                    speechAndTextAnalyticsService
                        .sentimentsList()
                        .find((sentiment) => {
                            return (
                                sentiment.topicNumber === payload.topicNumber
                            );
                        });
                if (
                    topicNumber !== undefined &&
                    phraseId !== undefined &&
                    location !== undefined
                ) {
                    this.publishJumpToPhrase({
                        topicNumber,
                        phraseId,
                    });
                    qualityBroadcast.publish(
                        this.recording().id,
                        "transcriptUIJumpToLocation",
                        {
                            transfer: location,
                        }
                    );
                }
            }
        }
        if (payload.key && payload.key === "toastNotification") {
            event.preventDefault();
            switch (payload.transfer.type) {
                case ToastNotificationTypeEnum.MESSAGE:
                    toastService.notifyMessage(
                        payload.transfer.title,
                        {
                            bodyMessageKey: payload.transfer.bodyMessageKey,
                            messageData: payload.transfer.messageData,
                        },
                        payload.transfer.useLongDelay
                    );
                    break;
                case ToastNotificationTypeEnum.MINOR_ALERT:
                    toastService.notifyMinorAlert(payload.transfer.title, {
                        bodyMessageKey: payload.transfer.bodyMessageKey,
                        messageData: payload.transfer.messageData,
                    });
                    break;
                case ToastNotificationTypeEnum.MAJOR_ALERT:
                    toastService.notifyMajorAlert(payload.transfer.title, {
                        bodyMessageKey: payload.transfer.bodyMessageKey,
                        messageData: payload.transfer.messageData,
                    });
                    break;
                case ToastNotificationTypeEnum.CRITICAL_ALERT:
                    toastService.notifyCriticalAlert(payload.transfer.title, {
                        bodyMessageKey: payload.transfer.bodyMessageKey,
                        messageData: payload.transfer.messageData,
                    });
                    break;
                default:
                    loggingService.error(
                        "Toast notification type was not provided by transcript UI"
                    );
            }
        }
    }

    onFrameLoaded(self: GIATranscriptView, event: Event) {
        this.childIFrameElement = event.target as HTMLIFrameElement;

        // Fallback to publish anyway if not given a response from Transcript UI
        setTimeout(this.publishTranscriptUIData.bind(this), 1000);

        window.addEventListener("message", (e) => {
            this.handleMessageEvent(e);
        });

        const syncWithTranscriptUI = () => {
            const mediaType = this.mediaType();
            const recording = this.recording();
            if (mediaType || recording) {
                this.publishActiveCommunication(mediaType, recording);

                if (recording && recording.id) {
                    qualityBroadcast.subscribe(
                        recording.id,
                        "transcriptUIAnnotationClick",
                        (message: number) => this.publishJumpToPhrase(message)
                    );
                    qualityBroadcast.subscribe(
                        recording.id,
                        "transcriptUIPositionUpdate",
                        (message: PlayerSynchEvent) =>
                            this.publishUpdatePosition(message)
                    );
                }
                this.isLoading(false);
            }
        };

        this.subscribe(this.recording, syncWithTranscriptUI);
        this.subscribe(this.isActiveInteraction, syncWithTranscriptUI);
        syncWithTranscriptUI();
    }
}
